import { useExpiredDocuments } from "../context/ExpiredDocumentsContext";
import { loadProlongationContracts, contractsEndingInWeek } from "../services/prolongationContracts";
import releveHeuresService from "../services/releveHeuresService";
import { untreatedTimesheetCount } from "../utils/timesheetFilters";
import AgencyChoice from "../components/AgencyChoice";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/reusableComponents/Dialog";
import React, { useState, useContext, useEffect } from "react";
import { motion } from "framer-motion";
import Logo2 from "../assets/img/jm-interim-transparent.png";
import { cn } from "../../src/utils/utils";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../../src/context/AuthContext";
import axios from "../axios/AxiosInstance";
import { apiErrorMessage } from "../utils/apiError";
import { rememberAgency } from "../services/agencyPreference";
import { clearAuthenticationStorage } from "../services/authSession";
import { message, Popconfirm } from "antd";

import { Home, Briefcase, ShoppingCart, Users, UserCheck, FileText, FileSignature, Calculator, Receipt, Library, UserCircle, LogOutIcon, Building, Bell, WatchIcon, CalendarPlus, CalendarDays, ShieldCheck, FileWarning } from "lucide-react";

export default function Sidebar({ path }) {
  const { total: expiredDocumentsTotal, loading: expiredDocumentsLoading, error: expiredDocumentsError } = useExpiredDocuments();
  const [prolongationCount, setProlongationCount] = useState(0);
  const [activeOrderCount, setActiveOrderCount] = useState(null);
  const [untreatedRhCount, setUntreatedRhCount] = useState(null);
  const [showAgencySwitch, setShowAgencySwitch] = useState(false); // Gère l'affichage de la modale
    const { setIsLoggedIn } = useContext(AuthContext);
  const navigate = useNavigate();
  const [switchingAgency, setSwitchingAgency] = useState(null);
  const [agencyCity, setAgencyCity] = useState("");
  const [agencyName, setAgencyName] = useState("");

  useEffect(() => {
    let active = true;
    const refresh = () => {
    loadProlongationContracts().then(contracts => { if (active) setProlongationCount(contractsEndingInWeek(contracts).length); }).catch(() => { if (active) setProlongationCount(0); });
    releveHeuresService.getMyReleveHeures()
      .then(rows => { if (active) setUntreatedRhCount(untreatedTimesheetCount(rows.map(row => releveHeuresService.transformToFrontendFormat(row)))); })
      .catch(() => { if (active) setUntreatedRhCount(null); });
    };
    refresh();
    window.addEventListener("focus", refresh);
    return () => { active = false; window.removeEventListener("focus", refresh); };
  }, [path]);

  useEffect(() => {
    let active = true;
    const refresh = () => axios.get('order/freelance/en-cours')
      .then(({ data }) => { if (active) setActiveOrderCount(Number.isInteger(data) && data >= 0 ? data : null); })
      .catch(() => { if (active) setActiveOrderCount(null); });
    refresh();
    window.addEventListener('focus', refresh);
    window.addEventListener('orders-changed', refresh);
    const timer = setInterval(refresh, 30000);
    return () => { active = false; clearInterval(timer); window.removeEventListener('focus', refresh); window.removeEventListener('orders-changed', refresh); };
  }, [path]);

  const isActive = (chemin) => {
    return chemin === path;
  };

  useEffect(() => {
    const name = localStorage.getItem("agencyName") || "";
    setAgencyName(["undefined", "null"].includes(name) ? "" : name.trim());
    const knownCity = ["Asnières", "Brive", "Toulouse"].find((city) =>
      name.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().includes(
        city.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
      )
    );
    setAgencyCity(localStorage.getItem("agencyCity") || knownCity || "");
  }, []);

  const navGroups = [
    {
      title: "Gestion",
      items: [
        { href: "/actions-et-taches", icon: Briefcase, label: "Actions et tâches" },
        { href: "/planning", icon: CalendarDays, label: "Planning" },
        { href: "/atradius", icon: ShieldCheck, label: "Atradius" },
        { href: "/annonces", icon: Bell, label: "Annonces" },
        { href: "/commandesPage", icon: ShoppingCart, label: "Commandes" },
        { href: "/prolongations", icon: CalendarPlus, label: "Prolongations" },
        { href: "/relevesdheures", icon: WatchIcon, label: "Relevés d’heures" },
      ],
    },
    {
      title: "Client",
      items: [
        { href: "/clients", icon: Users, label: "Mes clients" },
        { href: "/contrats-clients", icon: FileSignature, label: "Contrats clients" },
        { href: "/devis", icon: Calculator, label: "Devis" },
        { href: "/facture", icon: Receipt, label: "Factures" },
      ],
    },
    {
      title: "Candidat",
      items: [
        { href: "/candidats-proposes", icon: UserCheck, label: "Suivi des CV proposés" },
        { href: "/candidatsPage", icon: UserCheck, label: "Mes candidats" },
        { href: "/documents-perimes", icon: FileWarning, label: "Documents périmés" },
        { href: "/contrats", icon: FileText, label: "Contrats intérimaires" },
      ],
    },
    {
      title: "Outils",
      items: [
        { href: "/mes-factures", icon: Receipt, label: "Mes factures" },
        { href: "/bibliotheque", icon: Library, label: "Bibliothèque" },
        { href: "/monCompte", icon: UserCircle, label: "Mon compte" },
      ],
    },
  ];

  // Fonction pour ouvrir la modale
  const handleSwitchAgencyClick = () => {
    setShowAgencySwitch(true); // Affiche la modale
  };

  const logout = () => {
    setIsLoggedIn(false);
    clearAuthenticationStorage();
    navigate("/");
  };

  const switchAgency = async (agency) => {
    if (switchingAgency) return;
    setSwitchingAgency(agency.id);
    try {
      await axios.post("agency/switch", { agency: agency.id });
      rememberAgency(agency.id, agency.name, agency.city);
      window.location.assign("/dashboard");
    } catch (error) {
      message.error(apiErrorMessage(error, error.response?.status === 403 || error.response?.data?.statusCode === 403 ? "Vous n’êtes pas autorisé à accéder à cette agence." : "Impossible de changer d’agence. Réessayez."));
      setSwitchingAgency(null);
    }
  };

  return (
    <motion.aside
      className="h-screen left-0 min-w-[280px] max-w-[280px] bg-slate-50 text-slate-800 flex flex-col shadow-xl shadow-slate-200/50 border-r border-slate-200"
      initial={{ x: -280 }}
      animate={{ x: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      {/* Logo et nom de l'agence */}
      <motion.div
        className="flex flex-col items-center py-5 bg-slate-50 text-blue-950 border-b border-slate-200 relative overflow-hidden flex-shrink-0"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.6 }}
      >
        <div className="relative z-10 flex flex-col items-center">
          <motion.div
            className="relative"
            whileHover={{ scale: 1.1 }}
            transition={{ duration: 0.3 }}
          >
            <img src={Logo2} alt="Logo JM Intérim" className="w-36 h-24 object-contain" />
          </motion.div>
          <motion.h2
            className="text-lg font-bold tracking-tight mt-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Espace recruteur
          </motion.h2>
          <motion.div
            className="mt-1 flex items-center space-x-2 bg-blue-100 px-3 py-1 rounded-full"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6 }}
          >
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
            <p className="text-xs text-blue-900 font-medium">{agencyName || { "Asnières": "Asnières (92)", "Asnieres": "Asnières (92)", "Toulouse": "Toulouse (31)", "Brive": "Brive (19)" }[agencyCity] || agencyCity || "Votre agence"}</p>
          </motion.div>
        </div>
      </motion.div>

      {/* Navigation quotidienne */}
      <nav aria-label="Menu principal" className="flex min-h-0 flex-1 flex-col overflow-hidden px-3 pt-4 pb-3">
        <div className="sidebar-scroll min-h-0 flex-1 space-y-5 overflow-y-auto pb-4 pr-1">
          <Link
            to="/dashboard"
            aria-current={isActive("/dashboard") ? "page" : undefined}
            className={cn(
              "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-400",
              isActive("/dashboard") ? "bg-blue-600 text-white" : "text-slate-700 hover:bg-blue-50 hover:text-blue-900"
            )}
          >
            <Home size={19} aria-hidden="true" /> Tableau de bord
          </Link>
          {navGroups.map((group) => (
            <div key={group.title}>
              <h3 className="mb-1.5 px-3 text-[11px] font-semibold uppercase tracking-widest text-slate-500">{group.title}</h3>
              <ul className="space-y-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.href}>
                      <Link
                        to={item.href}
                        aria-current={isActive(item.href) ? "page" : undefined}
                        className={cn(
                          "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-400",
                          isActive(item.href) ? "bg-blue-600 font-semibold text-white" : "text-slate-700 hover:bg-blue-50 hover:text-blue-900"
                        )}
                      >
                        <Icon size={19} className="shrink-0" aria-hidden="true" />
                        <span className="min-w-0 flex-1">{item.label}</span>
                        {item.href === "/commandesPage" && activeOrderCount > 0 && <span aria-label={`${activeOrderCount} commandes ni annulées ni terminées`} style={{ marginLeft: "auto", flexShrink: 0 }} className={cn("ml-auto rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums", isActive(item.href) ? "bg-white/20 text-white" : "bg-blue-100 text-blue-800")}>{activeOrderCount}</span>}
                        {item.href === "/prolongations" && prolongationCount > 0 && <span aria-label={`${prolongationCount} contrats à échéance cette semaine`} title="Contrats qui se terminent cette semaine" className={cn("ml-auto rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums", isActive(item.href) ? "bg-white/20 text-white" : "bg-orange-100 text-orange-800")}>{prolongationCount}</span>}
                        {item.href === "/relevesdheures" && untreatedRhCount > 0 && <span aria-label={`${untreatedRhCount} relevés d’heures non traités cette semaine`} title="Relevés à saisir, en attente ou à valider cette semaine" className={cn("ml-auto rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums", isActive(item.href) ? "bg-white/20 text-white" : "bg-amber-100 text-amber-800")}>{untreatedRhCount}</span>}
                        {item.href === "/documents-perimes" && !expiredDocumentsLoading && expiredDocumentsTotal > 0 && <span
                          aria-label={expiredDocumentsLoading ? "Calcul du total en cours" : `${expiredDocumentsTotal} documents périmés${expiredDocumentsError ? ", vérification incomplète" : ""}`}
                          title={expiredDocumentsError || "Nombre total de documents périmés"}
                          className={cn("ml-auto rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums", isActive(item.href) ? "bg-white/20 text-white" : "bg-red-100 text-red-700")}
                        >{expiredDocumentsLoading ? "…" : `${expiredDocumentsTotal}${expiredDocumentsError ? "+" : ""}`}</span>}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* Actions Section */}
        <div className="space-y-2 border-t border-slate-200 pt-3 flex-shrink-0">
          <div className="mb-2 px-3">
            <h3 className="text-xs uppercase text-slate-500 font-semibold tracking-widest">
              Actions
            </h3>
          </div>

          <motion.button
            onClick={handleSwitchAgencyClick}
            className="w-full flex items-center px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-900 rounded-lg transition-all duration-300 group"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Building className="w-4 h-4 mr-3 text-slate-500 group-hover:text-blue-700 transition-colors duration-300" />
            <span className="font-medium">Changer d’agence</span>
            <span className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-slate-500">
              ⇄
            </span>
          </motion.button>

          <Popconfirm
            title="Êtes-vous sûr de vouloir vous déconnecter ?"
            onConfirm={logout}
            okText="Oui"
            cancelText="Non"
            placement="topRight"
          >
            <motion.button
              className="w-full flex items-center px-3 py-2 text-sm text-slate-600 hover:bg-rose-50 hover:text-rose-700 rounded-lg transition-all duration-300 group"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <LogOutIcon className="w-4 h-4 mr-3 text-slate-500 group-hover:text-rose-600 transition-colors duration-300" />
              <span className="font-medium">Déconnexion</span>
              <span className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-red-400">
                ↗
              </span>
            </motion.button>
          </Popconfirm>
        </div>
      </nav>

      <Dialog open={showAgencySwitch} onOpenChange={open => { if (!switchingAgency) setShowAgencySwitch(open); }}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-lg rounded-2xl bg-white p-6">
          <DialogHeader><DialogTitle>Choisir une agence</DialogTitle></DialogHeader>
          <p className="mb-5 mt-2 text-sm text-slate-500">Ouvrez l’espace de l’agence pour laquelle vous travaillez.</p>
          <AgencyChoice currentCity={agencyCity} busy={switchingAgency} onChoose={switchAgency} />
          <button type="button" disabled={Boolean(switchingAgency)} onClick={() => setShowAgencySwitch(false)} className="mt-5 rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50">Annuler</button>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <div className="py-3 px-6 border-t border-slate-200 flex-shrink-0">
        <div className="w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent"></div>
      </div>
    </motion.aside>
  );
}
